import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Phone, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { businessReference, SITE_URL } from "../seo/business";
import { getBlogPostBySlug, getBlogPosts, getCompanyInfo } from "../services/api";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postData, blogPostsData, companyInfoData] = await Promise.all([
          getBlogPostBySlug(slug),
          getBlogPosts(),
          getCompanyInfo()
        ]);
        
        setPost(postData);
        setBlogPosts(blogPostsData);
        setCompanyInfo(companyInfoData);
      } catch (error) {
        console.error("Error fetching blog post:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div>
        <SEO
          title="Post Not Found | Septic Guides"
          robots="noindex, follow"
          description="The blog post you were looking for could not be found. Browse all Groundhogs Septic Systems guides and articles."
          keywords="septic guides, septic blog, Thunder Bay septic articles"
        />
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link
            to="/blog"
            className="hover:opacity-70"
            style={{ color: "var(--primary-orange)" }}
          >
            Return to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Get related posts
  const relatedPosts = blogPosts.filter(p => p.id !== post.id && p.category === post.category).slice(0, 2);

  const isTankVsField = slug === "septic-tank-vs-septic-field-difference";

  const tankVsFieldFaq = isTankVsField
    ? [
        {
          question: "What&apos;s the difference between a septic tank and a septic field?",
          answer:
            "The septic tank separates solids and provides primary treatment; the septic field (leaching bed/drain field) disperses and treats effluent in soil. Both must work together for a healthy system.",
        },
        {
          question: "Which part fails more often in Thunder Bay-area conditions?",
          answer:
            "Both can fail, but drain fields often fail first due to soil limitations (clay, high groundwater), age, or hydraulic overload. A site review helps determine whether repair or replacement is the right path.",
        },
        {
          question: "Can you replace just the septic tank and keep the field?",
          answer:
            "Sometimes. If the field is still functioning and compliant, a tank-only replacement may be possible. We assess the field and explain options before work begins.",
        },
        {
          question: "What are signs of a failing septic field vs a failing tank?",
          answer:
            "Field issues often show as soggy, smelly areas or surfacing effluent; tank issues often show as structural cracks, missing baffles, unsafe lids, or inlet/outlet problems. Backups can come from either.",
        },
        {
          question: "When should I plan for septic system replacement?",
          answer:
            "If the system is 20–30+ years old with recurring backups, a saturated field, or multiple component failures, replacement is often the most cost-effective long-term solution.",
        },
      ]
    : [];

  const blogKeywords = isTankVsField
    ? "septic tank vs septic field, septic field vs septic tank, septic system replacement Thunder Bay, septic tank replacement Thunder Bay, septic field replacement Thunder Bay"
    : "septic installation Thunder Bay, septic guide";

  const blogDescription = isTankVsField
    ? "Learn the difference between a septic tank and septic field (leaching bed), how each fails, and when replacement makes sense for Thunder Bay and Northwestern Ontario properties."
    : post.metaDescription || post.excerpt.substring(0, 150);

  const normalizedTitle = (t) => {
    if (!t) return "";
    // Avoid duplicate or malformed title endings (e.g., "| Thu | Thunder Bay")
    const cleaned = t.replace(/\s*\|\s*Thunder Bay\s*$/i, "").trim();
    return cleaned;
  };

  const blogTitle = isTankVsField
    ? "Septic Tank vs Septic Field: Differences + Failure Signs (Thunder Bay)"
    : (() => {
        const base = normalizedTitle(post.metaTitle || post.title);
        if (!base) return "Thunder Bay";
        // If the title already contains Thunder Bay, don't append it again.
        if (/thunder bay/i.test(base)) return base;
        return `${base} | Thunder Bay`;
      })();

  return (
    <div>
      <SEO 
        title={blogTitle}
        description={blogDescription}
        keywords={blogKeywords}
        image={post.image}
        pageType="article"
      />
      
      {/* Article Structured Data */}
      <StructuredData id="blog-article-schema" type="Article" data={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": blogDescription,
          "image": post.image,
          "author": businessReference,
          "publisher": businessReference,
          "datePublished": post.date,
          "dateModified": post.date,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${SITE_URL}/blog/${post.slug}`
          }
        }} />

      {/* FAQ Structured Data (only for the tank vs field post) */}
      {isTankVsField && tankVsFieldFaq.length > 0 && (
        <StructuredData type="FAQPage" id="blog-faq-schema" data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": tankVsFieldFaq.map((item) => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer,
              },
            })),
          }} />
      )}
      
      <Header />

      {/* Hero Section */}
      <section
        className="gh-intro gh-blog-intro pt-32 pb-16 relative"
        style={{
          backgroundImage: `url('${post.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(28, 43, 58, 0.85)" }} />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <div className="mb-6">
            <div style={{ filter: 'brightness(0) invert(1)' }}>
              <Breadcrumbs customTitle={post.title} />
            </div>
          </div>
          
          <Link to="/blog" className="inline-flex items-center space-x-2 text-white mb-6 hover:opacity-70">
            <ArrowLeft size={20} />
            <span>Back to Blog</span>
          </Link>
          <div className="inline-block px-4 py-1 rounded-full mb-4" style={{ backgroundColor: "var(--primary-orange)", color: "white" }}>
            <span className="text-sm font-semibold">{post.category}</span>
          </div>
          <h1 className="text-5xl font-bold mb-4 text-white">
            {post.title}
          </h1>
          <div className="flex items-center space-x-6 text-white">
            <div className="flex items-center space-x-2">
              <Calendar size={20} />
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={20} />
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" style={{ color: "var(--text-dark)" }}>
            {/* Render actual blog content from API */}
            {post.content && (
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            )}
            
            {/* Fallback for posts without content */}
            {!post.content && post.id === 1 && (
              <div>
                <p className="text-xl mb-6" style={{ color: "var(--text-dark)" }}>
                  Planning a septic system installation in Thunder Bay? Understanding the costs involved is crucial for budgeting your project. Here&apos;s everything you need to know about septic installation pricing in 2025.
                </p>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>Traditional Septic System Costs</h2>
                <p className="mb-4">
                  Traditional septic systems in Thunder Bay typically cost between <strong>$25,000 and $60,000</strong> for a complete installation. This includes:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Site evaluation and system design</li>
                  <li>All permits and inspections</li>
                  <li>Septic tank (concrete or plastic)</li>
                  <li>Distribution box and piping</li>
                  <li>Drain field installation</li>
                  <li>Excavation and backfilling</li>
                  <li>Final grading</li>
                </ul>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>Eljen Mound System Costs</h2>
                <p className="mb-4">
                  Advanced Eljen mound systems range from <strong>$20,000 to $50,000</strong>. While the price range overlaps with traditional systems, Eljen systems offer significant advantages:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li><strong>Smaller footprint:</strong> Requires much less space than traditional systems</li>
                  <li><strong>Less sand import:</strong> Reduces material costs and site disruption</li>
                  <li><strong>Ideal for challenging sites:</strong> Works with high water tables and poor soil</li>
                  <li><strong>Faster installation:</strong> Can be completed in about 3 days on average</li>
                </ul>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>What Affects Septic Installation Cost?</h2>
                <p className="mb-4">Several factors influence your final septic installation cost:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li><strong>Property Size:</strong> Larger homes require bigger tanks and drain fields</li>
                  <li><strong>Soil Conditions:</strong> Clay, rock, or high water tables increase complexity</li>
                  <li><strong>Site Accessibility:</strong> Difficult access requires specialized equipment</li>
                  <li><strong>System Type:</strong> Traditional vs. Eljen mound systems have different requirements</li>
                  <li><strong>Permit Requirements:</strong> Ministry of Environment approvals add complexity</li>
                  <li><strong>Distance from Road:</strong> Longer distances increase excavation costs</li>
                </ul>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>Financing Options</h2>
                <p className="mb-6">
                  Understanding that septic installation is a significant investment, we offer in-house financing and flexible payment plans in specific scenarios. Contact us to discuss options that fit your budget.
                </p>

                <div className="p-6 rounded-lg mb-6" style={{ backgroundColor: "var(--light-gray)" }}>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>Get Your Fast Virtual Estimate</h3>
                  <p className="mb-4">
                    We provide detailed virtual estimates based on your property information - all handled remotely, with no in-person visit required.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-block px-6 py-3 rounded-md font-bold text-white hover:opacity-90"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    Request Free Estimate
                  </Link>
                </div>
              </div>
            )}

            {post.id === 2 && (
              <div>
                <p className="text-xl mb-6" style={{ color: "var(--text-dark)" }}>
                  Choosing between a traditional septic system and an Eljen mound system is one of the most important decisions for your property. Here&apos;s what you need to know.
                </p>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>The Space Advantage: Why Most Choose Eljen</h2>
                <p className="mb-6">
                  The #1 reason Thunder Bay property owners choose Eljen mound systems: <strong>space constraints</strong>. Eljen systems require <strong>much smaller field sizes</strong> - sometimes 50% less area than traditional systems. This is crucial for:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Small lot sizes common in Northwestern Ontario</li>
                  <li>Properties with limited buildable area</li>
                  <li>Sites where you want to preserve yard space</li>
                  <li>Waterfront properties with setback restrictions</li>
                </ul>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>Reducing Sand Import Costs</h2>
                <p className="mb-6">
                  Another major advantage: Eljen systems significantly reduce the amount of sand that needs to be imported to your site. This means:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Lower material costs</li>
                  <li>Less truck traffic to your property</li>
                  <li>Reduced site disruption</li>
                  <li>Faster installation (often about 3 days on average)</li>
                </ul>

                <h2 className="text-3xl font-bold mb-4 mt-8" style={{ color: "var(--dark-navy)" }}>When Traditional Systems Work Best</h2>
                <p className="mb-6">
                  Traditional septic systems are ideal when you have:
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Large property with plenty of space</li>
                  <li>Good soil conditions with adequate percolation</li>
                  <li>Low water table</li>
                  <li>Easy site access for equipment</li>
                </ul>

                <div className="p-6 rounded-lg mb-6" style={{ backgroundColor: "var(--primary-orange)", color: "white" }}>
                  <h3 className="text-2xl font-bold mb-3">Not Sure Which System You Need?</h3>
                  <p className="mb-4">
                    Contact us for a fast virtual assessment. We&apos;ll evaluate your property details and recommend the best system for your situation.
                  </p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="inline-block px-6 py-3 rounded-md font-bold bg-white hover:opacity-90"
                    style={{ color: "var(--primary-orange)" }}
                  >
                    Call {companyInfo.phone}
                  </a>
                </div>
              </div>
            )}

            {/* Add content for other posts similarly */}
            {post.id >= 3 && (
              <div>
                <p className="text-xl mb-6" style={{ color: "var(--text-dark)" }}>
                  {post.excerpt}
                </p>
                <p className="mb-6">
                  For detailed information about this topic, please contact our team at <strong>{companyInfo.phone}</strong>. We&apos;re happy to answer all your questions about septic system installation in Thunder Bay and Northwestern Ontario.
                </p>
                <div className="p-6 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>Get Expert Advice</h3>
                  <p className="mb-4">
                    Our team has completed 1,200+ septic installations with a 100% permit approval rate. Get your fast virtual estimate today.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-block px-6 py-3 rounded-md font-bold text-white hover:opacity-90"
                    style={{ backgroundColor: "var(--primary-orange)" }}
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Content Section */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* High-Intent Service Links */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-10">
            <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
              Need a Septic System in Thunder Bay?
            </h3>
            <p className="mb-4" style={{ color: "var(--text-light)" }}>
              If you&apos;re reading this article because you suspect your current system is nearing the end of its
              life, or you&apos;re planning a new build, our team can help with everything from design to full
              installation and replacement.
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <Link
                to="/services/septic-system-installation-thunder-bay"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                View Septic System Installation in Thunder Bay
              </Link>
              <Link
                to="/services/septic-system-replacement-thunder-bay"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold border-2 hover:bg-gray-50"
                style={{ borderColor: "var(--primary-orange)", color: "var(--dark-navy)" }}
              >
                Learn About Septic System Replacement
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <>
              <h2 className="text-3xl font-bold mb-8" style={{ color: "var(--dark-navy)" }}>Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl"
                    style={{ transition: "box-shadow 0.3s ease" }}
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover hover:scale-105"
                        style={{ transition: "transform 0.3s ease" }}
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 hover:opacity-70" style={{ color: "var(--dark-navy)" }}>
                        {relatedPost.title}
                      </h3>
                      <p style={{ color: "var(--text-light)" }}>{relatedPost.excerpt.substring(0, 120)}...</p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
          
          {/* Related Service Pages */}
          <div className="bg-white p-8 rounded-lg shadow-md mb-8">
            <h3 className="text-2xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>Our Septic Services</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/services/septic-tank-installation-thunder-bay"
                className="flex items-center space-x-3 p-4 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: "var(--light-gray)" }}
              >
                <span className="text-2xl">🛢️</span>
                <div>
                  <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Septic Tank Installation &amp; Replacement</div>
                  <div className="text-sm" style={{ color: "var(--text-light)" }}>Thunder Bay</div>
                </div>
              </Link>
              <Link
                to="/services/septic-field-installation-replacement-thunder-bay"
                className="flex items-center space-x-3 p-4 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: "var(--light-gray)" }}
              >
                <span className="text-2xl">🌿</span>
                <div>
                  <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Septic Field Installation &amp; Replacement</div>
                  <div className="text-sm" style={{ color: "var(--text-light)" }}>Thunder Bay</div>
                </div>
              </Link>
              <Link
                to="/septic-calculator"
                className="flex items-center space-x-3 p-4 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: "var(--primary-orange)", color: "white" }}
              >
                <span className="text-2xl">💰</span>
                <div>
                  <div className="font-semibold">Free Estimate</div>
                  <div className="text-sm opacity-90">Get instant pricing</div>
                </div>
              </Link>
            </div>
          </div>
          
          {/* Popular Service Areas */}
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h3 className="text-2xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>Serving Northwestern Ontario</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/service-areas/thunder-bay" className="text-center p-3 rounded-lg hover:shadow-md transition-all" style={{ backgroundColor: "var(--light-gray)" }}>
                <MapPin size={24} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-2" />
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Thunder Bay</div>
              </Link>
              <Link to="/service-areas/kenora" className="text-center p-3 rounded-lg hover:shadow-md transition-all" style={{ backgroundColor: "var(--light-gray)" }}>
                <MapPin size={24} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-2" />
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Kenora</div>
              </Link>
              <Link to="/service-areas/dryden" className="text-center p-3 rounded-lg hover:shadow-md transition-all" style={{ backgroundColor: "var(--light-gray)" }}>
                <MapPin size={24} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-2" />
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Dryden</div>
              </Link>
              <Link to="/service-areas/fort-frances" className="text-center p-3 rounded-lg hover:shadow-md transition-all" style={{ backgroundColor: "var(--light-gray)" }}>
                <MapPin size={24} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-2" />
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>Fort Frances</div>
              </Link>
            </div>
            <div className="text-center mt-6">
              <Link
                to="/service-areas"
                className="inline-block px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                View All 28 Service Areas →
              </Link>
            </div>
          </div>
          
          {/* Link to Blog Index */}
          {relatedPosts.length > 0 && (
            <div className="text-center mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-lg transition-all hover:scale-105"
                style={{ 
                  backgroundColor: 'var(--primary-orange)',
                  color: 'white'
                }}
              >
                View All Articles →
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;